import { useState } from "react";
import toast from "react-hot-toast";
import { FiSearch, FiX, FiCheck } from "react-icons/fi";
import { MdOutlinePushPin } from "react-icons/md";

import ListingThumb from "./ListingThumb";
import { usePinnableCandidates, useAddPinnedItems } from "../../hooks/pinned";
import { useDebounce } from "../../hooks/useDebounce";
import { useSignedUrlMap } from "../../hooks/useSignedUrlMap";

/**
 * Side panel for pinning listings.
 *
 * Only live, approved listings that are not pinned yet can appear here — the
 * server enforces the same rule, so the panel can never offer something the
 * pin request would reject. Picks accumulate so a rail can be built in one
 * pass, and land at the end of the order rather than disturbing it.
 */
const AddPinnedListings = ({ section, sectionLabel, onPinned, onCancel }) => {
  const [term, setTerm] = useState("");
  const [selected, setSelected] = useState([]);

  const debouncedTerm = useDebounce(term);
  const { data: candidates = [], isFetching, error } = usePinnableCandidates(
    section,
    debouncedTerm
  );
  const { mutate: addPinnedItems, isPending } = useAddPinnedItems(section);

  const images = useSignedUrlMap(candidates.map((item) => item.image));
  const selectedIds = new Set(selected.map((item) => item._id));

  const toggle = (item) =>
    setSelected((current) =>
      selectedIds.has(item._id)
        ? current.filter((picked) => picked._id !== item._id)
        : [...current, item]
    );

  const submit = () => {
    if (selected.length === 0) return;

    addPinnedItems(
      selected.map((item) => ({ id: item._id, category: item.category })),
      {
        onSuccess: (data) => {
          // Nothing pinned means the panel was working from a stale list —
          // everything picked is pinned already, or is no longer live.
          if (data?.pinned?.length) toast.success(data.message);
          else toast(data?.message || "Nothing to pin");
          onPinned();
        },
        onError: (pinError) => toast.error(pinError.message),
      }
    );
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-1 pb-4 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-lg bg-primary8 text-primary flex items-center justify-center">
            <MdOutlinePushPin className="text-lg" />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Pin {sectionLabel}</h2>
            <p className="text-xs text-gray-500">
              Pinned listings are added to the end of the order.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto py-4 px-1 space-y-3">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder="Search by name, city or area"
            className="w-full border border-gray-300 rounded-lg py-2.5 pl-9 pr-9 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          />
          {term && (
            <button
              type="button"
              onClick={() => setTerm("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <FiX />
            </button>
          )}
        </div>

        {selected.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selected.map((item) => (
              <span
                key={item._id}
                className="inline-flex items-center gap-1.5 bg-primary8 text-primary rounded-full pl-2.5 pr-1.5 py-1 text-xs font-medium max-w-full"
              >
                <span className="truncate">{item.name || "Unnamed listing"}</span>
                <button
                  type="button"
                  onClick={() => toggle(item)}
                  aria-label={`Remove ${item.name || "listing"}`}
                  className="p-0.5 rounded-full hover:bg-white/70 shrink-0"
                >
                  <FiX className="text-[13px]" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="max-h-[52vh] overflow-y-auto divide-y divide-gray-100">
            {error && (
              <p className="px-4 py-6 text-sm text-red-600 text-center">{error.message}</p>
            )}

            {!error && candidates.length === 0 && (
              <p className="px-4 py-10 text-sm text-gray-500 text-center">
                {isFetching
                  ? "Searching listings..."
                  : "No live listings left to pin for this search"}
              </p>
            )}

            {candidates.map((item) => {
              const isSelected = selectedIds.has(item._id);
              return (
                <button
                  key={item._id}
                  type="button"
                  onClick={() => toggle(item)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                    isSelected ? "bg-primary8" : "hover:bg-gray-50"
                  }`}
                >
                  <ListingThumb
                    src={images.get(item.image)}
                    alt={item.name}
                    category={item.category}
                    className="w-14 h-11 rounded-lg shrink-0"
                  />

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {item.name || "Unnamed listing"}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {[item.city, item.area].filter(Boolean).join(" · ") || "No location"}
                    </p>
                    <p className="text-[11px] text-gray-400 truncate">
                      {item.category}
                      {item.vendorEmail ? ` · ${item.vendorEmail}` : ""}
                    </p>
                  </div>

                  <span
                    className={`shrink-0 w-5 h-5 rounded-md border flex items-center justify-center ${
                      isSelected
                        ? "bg-primary border-primary text-white"
                        : "border-gray-300 text-transparent"
                    }`}
                  >
                    <FiCheck className="text-[13px]" />
                  </span>
                </button>
              );
            })}
          </div>

          <p className="px-3 py-2 bg-gray-50 border-t border-gray-100 text-[11px] text-gray-500">
            {isFetching && "Searching..."}
            {!isFetching &&
              (debouncedTerm
                ? `${candidates.length} match${candidates.length === 1 ? "" : "es"} — narrow the search if the listing is missing`
                : "Showing the most recently updated listings — search to find a specific one")}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 px-1 pt-4 border-t border-gray-100">
        <span className="text-sm text-gray-500">
          {selected.length > 0
            ? `${selected.length} selected`
            : "Pick at least one listing"}
        </span>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={selected.length === 0 || isPending}
            className="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Pinning..." : "Pin listings"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPinnedListings;
